/**
 * Apply theme colours as CSS custom properties
 * This allows all CSS files to use var(--primary-colour) instead of hardcoded values
 * 
 * Following SOLID principles:
 * - Single Responsibility: Only responsible for applying theme to DOM
 * - Open/Closed: Can extend with new CSS properties without modifying existing code
 * - Dependency Inversion: Depends on AppSettings interface, not concrete implementation
 */

import { AppSettings } from '../services/directus/directusClient';
import { getTextColourForBackground } from './colourUtils';
import { THEME_DEFAULTS, CSS_VARS } from '../constants/themeDefaults';

export const applyThemeColours = (settings: AppSettings | null): void => {
  const root = document.documentElement;
  
  // Primary colour
  const primaryColour = settings?.primary_colour || THEME_DEFAULTS.PRIMARY_COLOUR;
  root.style.setProperty(CSS_VARS.PRIMARY_COLOUR, primaryColour);
  
  // Primary text colour (auto-calculated if not set)
  const primaryTextColour = settings?.primary_text_colour 
    || getTextColourForBackground(primaryColour);
  root.style.setProperty(CSS_VARS.PRIMARY_TEXT_COLOUR, primaryTextColour);
  
  // Secondary colour
  const secondaryColour = settings?.secondary_colour || THEME_DEFAULTS.SECONDARY_COLOUR;
  root.style.setProperty(CSS_VARS.SECONDARY_COLOUR, secondaryColour);
  
  // Derived colours for gradients and effects
  root.style.setProperty(CSS_VARS.PRIMARY_COLOUR_LIGHT, lightenColour(primaryColour, 20));
  root.style.setProperty(CSS_VARS.PRIMARY_COLOUR_DARK, darkenColour(primaryColour, 10));
  root.style.setProperty(CSS_VARS.PRIMARY_COLOUR_ALPHA_30, `${primaryColour}4D`); // 30% opacity
  root.style.setProperty(CSS_VARS.PRIMARY_COLOUR_ALPHA_20, `${primaryColour}33`); // 20% opacity
  
  console.log('[applyThemeColours] Applied theme colours:', {
    primary: primaryColour,
    primaryText: primaryTextColour,
    secondary: secondaryColour
  });
};

/**
 * Lighten a hex colour by a percentage
 */
const lightenColour = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

/**
 * Darken a hex colour by a percentage
 */
const darkenColour = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
  const B = Math.max(0, (num & 0x0000FF) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};
